import clsx from "clsx";
import PropTypes from "prop-types";
import { Avatar, AvatarGroup, Badge, Box, ListItem, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(theme => ({
  listItem: {
    backgroundColor: theme.palette.common.white,
    padding: 5,
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
    justifyContent: "space-between",
    minHeight: 36,
    fontSize: 12,
    "& > div > button": {
      display: "none",
    },
    "&:hover > div > button": {
      display: "block",
    },
  },
  listItemActive: {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  listItemUnread: {
    fontWeight: 600,
  },
  statusIndicator: {
    width: 5,
    height: 5,
    backgroundColor: "inherit",
    borderRadius: "50%",
    marginRight: 8,
    display: "inline-block",
    verticalAlign: "middle",
  },
  //statusOnline: {
  //  backgroundColor: "#3FA844",
  //},
  //statusOffline: {
  //  backgroundColor: "#a7a7a7",
  //},
  //deleteIconContainer: {
  //  minWidth: 26,
  //  paddingRight: 5,
  //},
  //deleteIcon: {},
  pendingConversation: {
    opacity: 0.6,
    backgroundColor: "#f5f5f5",
    fontStyle: "italic",
  },
  unreadMessageCount: {
    backgroundColor: theme.palette.component.badgeBackground,
    borderRadius: "50%",
    color: theme.palette.component.badgeColor,
    fontSize: 10,
    fontWeight: 700,
    aspectRatio: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 17,
  },
}));

const ConversationListItem = ({
  activeConversationId,
  conversation,
  onAddConversation,
  onConversationSelected,
  onAddConversationWithApproval,
  onPendingConversationSelected
}) => {
  const classes = useStyles();

  const {
    directMessageConversationId,
    latestMessageId,
    members,
    unreadMessageCount,
    pending
  } = conversation;

  const isActive = directMessageConversationId === activeConversationId;
  const isUnread = unreadMessageCount > 0;
  const channelName = members.map(n => (n.name || "").trim() || n.username).join(", ");
  const tooltipTitle = members.map(n => n.username).join(", ");

  return (
    <ListItem
      button
      disableRipple
      className={clsx([
        classes.listItem,
        isActive && classes.listItemActive,
        isUnread && classes.listItemUnread,
        conversation.pending && classes.pendingConversation,
      ])}
      onClick={() => {

        if (pending) {
          onPendingConversationSelected(conversation);
        }
        else if (directMessageConversationId) {
          onConversationSelected(directMessageConversationId);
        }
        else if (members[0].requireApproval)
        {          
          onAddConversationWithApproval(members[0]);
        }
        else {
          onAddConversation(members[0].userId);
        }
      }}
    >
      <Tooltip
        arrow
        placement="top"
        title={tooltipTitle}
        enterDelay={300}
        leaveDelay={100}
        disableFocusListener
        disableTouchListener
        disableInteractive
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -8],
                },
              },
            ],
          },
        }}
      >
        <Box style={{ display: "flex", flexDirection: "row", width: "100%" }}>
          <Box style={{ display: "flex", alignItems: "center" }}>
            <span className={clsx([classes.statusIndicator])} />

            <AvatarGroup max={2} spacing="small"> 
              {members.map((user, index) => {
                const avatar = (
                  <Avatar
                    key={"avatar" + index}
                    alt={user.name}
                    src={user.avatarUrl}
                    sx={{
                      backgroundColor: "#fff",
                      height: "30px",
                      width: "30px",
                      "&.MuiAvatar-root": {
                        borderColor: "#eee",
                      },
                    }}
                  />
                );

                if (index > 0) {
                  return avatar;
                }

                const maxCount = 9;

                return (
                  <Badge key={"badge" + index}
                    badgeContent={isUnread && !isActive && (
                      <span className={classes.unreadMessageCount}>
                        {(unreadMessageCount > maxCount) ? maxCount + "+" : unreadMessageCount}
                      </span>
                    )}
                    component="div"
                    key={user.userId}
                    overlap="circular"
                  >
                    {avatar}
                  </Badge>
                );
              })}
            </AvatarGroup>

            <Box sx={{ ml: 1 }}>
              <Typography variant="inherit">{channelName}</Typography>
            </Box>
          </Box>
        </Box>
      </Tooltip>
    </ListItem>
  );
};

ConversationListItem.defaultProps = {
  activeConversationId: null,
};

ConversationListItem.propTypes = {
  activeConversationId: PropTypes.number,
  conversation: PropTypes.shape({}).isRequired,
  onAddConversation: PropTypes.func.isRequired,
  onConversationSelected: PropTypes.func.isRequired,
};

export default ConversationListItem;
